import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useApiQuery } from "../hooks";
import type { ApiConfig, AppConfig } from "../types";

type Props = {
  label?: string;
  placeholder?: string;
  value: string | null;
  onChange: (appConfigId: string | null) => void;
  onConfigChange?: (config: AppConfig | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  helperText?: string;
  error?: boolean;
};

export const AppConfigSelect = ({
  label = "Select App Config",
  placeholder = "Search and select an app config...",
  value,
  onChange,
  onConfigChange,
  disabled = false,
  size = "medium",
  fullWidth = true,
  helperText,
  error = false,
}: Props) => {
  const { data: apiConfig, isLoading: isLoadingConfigs } = useApiQuery<ApiConfig>("/config");

  const appConfigs = useMemo((): AppConfig[] => {
    return apiConfig?.configs ?? [];
  }, [apiConfig]);

  const [searchQuery, setSearchQuery] = useState("");

  // Create Fuse instance for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(appConfigs ?? [], {
      keys: ["name", "base_url", "cfg_type"],
      threshold: 0.3,
      includeScore: true,
    });
  }, [appConfigs]);

  // Filter app configs based on search query
  const filteredConfigs = useMemo(() => {
    if (appConfigs.length === 0) return [];

    if (!searchQuery.trim()) {
      return appConfigs;
    }

    return fuse
      .search(searchQuery, { limit: 20 })
      .map(({ item }) => item)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [appConfigs, searchQuery, fuse]);

  // Find the currently selected app config
  const selectedConfig = useMemo(() => {
    if (!value || appConfigs.length === 0) return null;
    return appConfigs.find((config) => config.id === value) || null;
  }, [value, appConfigs]);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || isLoadingConfigs}
      loading={isLoadingConfigs}
      options={filteredConfigs}
      getOptionLabel={(option) => option.name || "Unnamed Config"}
      getOptionKey={(option) => option.id}
      value={selectedConfig}
      onChange={(_, newValue) => {
        onChange(newValue?.id || null);
        onConfigChange?.(newValue || null);
        setSearchQuery(""); // Reset search query after selection
      }}
      onInputChange={(_, newInputValue) => {
        setSearchQuery(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              fontSize: "0.875rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputBase-input": {
              padding: "8px 12px",
              fontSize: "0.875rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.875rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
            },
            "& .MuiFormHelperText-root": {
              fontSize: "0.75rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoadingConfigs ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <Box key={key} component="li" {...otherProps}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Stack
                direction="column"
                spacing={0}
                alignItems="flex-start"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                >
                  {option.name}
                </Typography>
                <Tooltip title={option.base_url ?? "about:blank"} placement="bottom-start">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.75rem",
                      fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "300px",
                    }}
                  >
                    {option.base_url ?? "about:blank"}
                  </Typography>
                </Tooltip>
              </Stack>
              <Chip label={option.cfg_type ?? "legacy"} size="small" />
            </Stack>
          </Box>
        );
      }}
      noOptionsText={
        searchQuery ? "No app configs found matching your search" : "No app configs available"
      }
      loadingText="Loading app configs..."
      clearOnBlur={false}
      clearOnEscape={false}
      selectOnFocus={false}
      blurOnSelect={true}
      openOnFocus={true}
      filterOptions={(x) => x} // Disable built-in filtering since we use Fuse
      sx={{
        "& .MuiAutocomplete-input": {
          cursor: "pointer",
        },
      }}
    />
  );
};
